import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Divider from '@Components/Divider/Divider';
import ConditionalRender from '@Components/ConditionalRender/ConditionalRender';
import {
	setActiveAddressBook,
	setNewAddressBook,
	setRefreshAddressBook,
	setOutletPickupFocus,
} from '@Redux/modules/addressBook/actions';
import { dispatch } from '@Redux/store';
import { setCheckoutDetails } from '@Redux/modules/checkout/actions';
import { BUY_HBB, BUY_WFBB } from '@Views/Map/Map';
import { IAddress } from '@ApiModels/commerceProfile';
import { useCart } from '@Utils/context/CartContext';
import useAddressBook from '@Utils/hooks/useAddressBook';
import { useOutlet } from '@Utils/context/OutletContext';
import { isSameAddress } from './util';
import HBBWFBB from './components/HBBWFBB';
import NotHBBWFBB from './components/NotHBBWFBB';
import OutletLocalisation from './components/OutletLocalisation';
import { ShippingMethodCardContainer } from './ShippingMethod.styled';

export const ICON_SIZE = 40;
export enum SelectedAddressType {
	ADRESS_BOOK = 'ADRESS_BOOK',
	DIFFERENT = 'DIFFERENT',
	PICKUP = 'PICKUP',
	OUTLET = 'OUTLET',
	SAME_AS_INTERNET_INSTALLATION = 'SAME_AS_INTERNET_INSTALLATION',
}

const ShippingMethod: FunctionComponent<types.cards.IShippingMethodCardProps> = ({
	params: {
		contactDetails,
		hasDevice,
		orderIncludesPlan,
		onlyPickupItems,
		launchDate,
		hbbWfbbInternetInstallationAddress,
		saveAddress,
		setAdditionalLandmark,
		setSaveAddress,
		isBuyHbbWfbbProcess,
		isPrepaidPurchase,
	},
}) => {
	const { cart } = useCart();
	const { selectedOutlet, availableOutlets } = useOutlet();

	const isLogged = useSelector((state: types.redux.IState) => state.api.authentication.signedIn);
	const activeAddressBook = useSelector((state: types.redux.IState) => state.addressBook.activeAddressBook);
	const newAddressBook = useSelector((state: types.redux.IState) => state.addressBook.newAddressBook);
	const hbbWfbbProcessCheckout = useSelector(
		(state: types.redux.IState) => state.persistentCheckout.hbbWfbbProcessCheckout
	);

	const [addressType, setAddressType] = useState<SelectedAddressType>(
		!!selectedOutlet.branchId
			? SelectedAddressType.OUTLET
			: availableOutlets.filter((item) => {
					return item.available === true;
			  }).length > 0
			? SelectedAddressType.OUTLET
			: isLogged
			? SelectedAddressType.ADRESS_BOOK
			: SelectedAddressType.DIFFERENT
	);
	const [lastModifiedAddressBook, setLastModifiedAddressBook] = useState<IAddress[]>([]);
	const {
		addressBook,
		addNewAddress,
		updateShippingAddress,
		addNewAddressWithoutModal,
		selectedAddress,
	} = useAddressBook();

	const hbbWfbbItem = useMemo(() => cart.items.find((item) => [BUY_HBB, BUY_WFBB].includes(item.plan?.PA || '')), [
		cart,
	]);
	const hasDeviceWithHbbWfbbItem = hasDevice && !!hbbWfbbItem;

	useEffect(() => {
		dispatch(setOutletPickupFocus({ outletPickupFocus: addressType === SelectedAddressType.OUTLET }));
	}, []);
	useEffect(() => {
		dispatch(setCheckoutDetails({ pickup: addressType === SelectedAddressType.PICKUP }));
	}, [addressType]);

	useEffect(() => {
		if (addressBook.length === 1 && addressType === SelectedAddressType.ADRESS_BOOK) {
			updateShippingAddress(addressBook[0]);
			dispatch(
				setActiveAddressBook({
					activeAddressBook: addressBook[0].addressId,
				})
			);
		} else {
			setLastModifiedAddressBook(addressBook);
			let newEditedAddress: IAddress | string = '';
			newEditedAddress = addressBook.find((newAddress) => newAddress.addressId === activeAddressBook) || '';
			if (activeAddressBook && !newEditedAddress) {
				addressBook.forEach((address) => {
					newEditedAddress =
						lastModifiedAddressBook.find((newAddress) => newAddress.addressId === address.addressId)
							?.addressId || '';
					if (!newEditedAddress) {
						if (newAddressBook) {
							newEditedAddress =
								addressBook.find((newAddress) => newAddress.addressId === newAddressBook) || '';
							if (!newEditedAddress) {
								dispatch(setNewAddressBook({ newAddressBook: address.addressId }));
							}
						}
						dispatch(setActiveAddressBook({ activeAddressBook: address.addressId }));
					}
				});
			}
		}
	}, [addressBook, addressType]);

	useEffect(() => {
		const sameAddress = isSameAddress(selectedAddress, hbbWfbbProcessCheckout);
		if (
			!sameAddress &&
			hbbWfbbInternetInstallationAddress &&
			addressType === SelectedAddressType.SAME_AS_INTERNET_INSTALLATION
		) {
			addNewAddressWithoutModal(hbbWfbbInternetInstallationAddress, contactDetails);
		}
	}, [addressBook, addressType, hbbWfbbInternetInstallationAddress]);

	useEffect(() => {
		if (addressType === SelectedAddressType.DIFFERENT) {
			const sameAddress = isSameAddress(selectedAddress, hbbWfbbProcessCheckout);
			if (sameAddress && hasDeviceWithHbbWfbbItem) {
				dispatch(
					setNewAddressBook({
						activeAddressBook: undefined,
					})
				);
			}
			const filtered = addressBook.filter((_add) => _add.addressId === newAddressBook);
			if (filtered.length > 0) {
				filtered.forEach((_address) => {
					if (newAddressBook === _address.addressId) {
						updateShippingAddress(_address);
						dispatch(
							setActiveAddressBook({
								activeAddressBook: _address.addressId,
							})
						);
					}
				});
			} else {
				dispatch(
					setActiveAddressBook({
						activeAddressBook: undefined,
					})
				);
				updateShippingAddress(undefined);
			}
		} else if (addressType === SelectedAddressType.ADRESS_BOOK) {
			dispatch(setRefreshAddressBook({}));
			if (addressBook.length > 1) {
				addressBook.forEach((_address) => {
					if (newAddressBook === _address.addressId) {
						dispatch(
							setActiveAddressBook({
								activeAddressBook: _address.addressId,
							})
						);
					}
					if (hasDeviceWithHbbWfbbItem) {
						if (activeAddressBook === _address.addressId) {
							updateShippingAddress(_address);
						}
					}
				});
			} else if (addressBook.length === 1) {
				updateShippingAddress(addressBook[0]);
				dispatch(
					setActiveAddressBook({
						activeAddressBook: addressBook[0].addressId,
					})
				);
			}
		}
	}, [addressType, newAddressBook]);

	return (
		<ShippingMethodCardContainer>
			<ConditionalRender
				show={!!hbbWfbbItem || !!hasDeviceWithHbbWfbbItem || !!isBuyHbbWfbbProcess}
				onTrue={() => (
					<HBBWFBB
						hbbWfbbItem={hbbWfbbItem}
						hbbWfbbInternetInstallationAddress={hbbWfbbInternetInstallationAddress}
						saveAddress={saveAddress}
						setAdditionalLandmark={setAdditionalLandmark}
						setSaveAddress={setSaveAddress}
					/>
				)}
			/>
			<ConditionalRender show={!isPrepaidPurchase} onTrue={() => <Divider withoutLine marginBottom={50} />} />
			<ConditionalRender
				show={!!hasDeviceWithHbbWfbbItem || !isBuyHbbWfbbProcess}
				onTrue={() => (
					<NotHBBWFBB
						addNewAddress={addNewAddress}
						addressBook={addressBook}
						addressType={addressType}
						hasDeviceWithHbbWfbbItem={hasDeviceWithHbbWfbbItem}
						hbbWfbbItem={hbbWfbbItem}
						isPrepaidPurchase={isPrepaidPurchase}
						launchDate={launchDate}
						orderIncludesPlan={orderIncludesPlan}
						onlyPickupItems={onlyPickupItems}
						setAddressType={setAddressType}
					/>
				)}
			/>
			<ConditionalRender
				show={addressType === SelectedAddressType.OUTLET}
				onTrue={() => <OutletLocalisation />}
			/>
		</ShippingMethodCardContainer>
	);
};

export default ShippingMethod;
